/**
 * KSO Analytics DataLayer Bootstrap
 * Initializes GTM dataLayer with concert and landing page metadata
 */

class KSOAnalytics {
    constructor() {
        this.isDebugMode = false; // Enable enhanced logging for debugging 
        this.environment = 'production'; // 'production' or 'development'
        this.gtmContainerId = null; // GTM Container ID from WP settings
        
        // Initialize dataLayer if it doesn't exist
        window.dataLayer = window.dataLayer || [];
        
        this.init();
    }

    /**
     * Initialize analytics system
     */
    async init() {
        try {
            // Get settings from WordPress
            await this.loadSettings();
            
            // Initialize dataLayer with page metadata
            this.initializeDataLayer();
            
            // Load GTM if container ID is provided
            if (this.gtmContainerId) {
                this.loadGTM();
            }
            
            // Set up event listeners for user interactions
            this.bindEvents();
            
            this.log('KSO Analytics initialized successfully');
        } catch (error) {
            console.error('KSO Analytics initialization failed:', error);
        }
    }

    /**
     * Load analytics settings from WordPress options
     */
    async loadSettings() {
        if (window.ksoAnalytics) {
            this.gtmContainerId = window.ksoAnalytics.gtmContainerId;
            this.gaTrackingId = window.ksoAnalytics.gaTrackingId;
            this.fbPixelId = window.ksoAnalytics.fbPixelId;
            this.isDebugMode = window.ksoAnalytics.debugMode;
            this.environment = window.ksoAnalytics.environment;
            
            this.log('Analytics settings loaded:', {
                gtmContainerId: this.gtmContainerId,
                gaTrackingId: this.gaTrackingId,
                fbPixelId: this.fbPixelId,
                debugMode: this.isDebugMode,
                environment: this.environment
            });
        }
    }

    /**
     * Initialize GTM dataLayer with page and concert metadata
     */
    initializeDataLayer() {
        const pageData = this.getPageMetadata();
        const concertData = this.getConcertMetadata();
        
        // Base dataLayer initialization
        const initData = {
            event: 'kso_page_loaded',
            page: pageData,
            environment: this.environment,
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            referrer: document.referrer || 'direct'
        };
        
        // Add concert data if available
        if (concertData.concertCount > 0) {
            initData.concert = concertData;
        }
        
        // Push to dataLayer
        window.dataLayer.push(initData);
        
        this.log('DataLayer initialized with:', initData);
    }

    /**
     * Extract page metadata for tracking
     */
    getPageMetadata() {
        const urlParams = new URLSearchParams(window.location.search);
        
        return {
            url: window.location.href,
            path: window.location.pathname,
            title: document.title,
            post_type: this.getPostType(),
            page_type: this.getPageType(),
            utm: {
                source: urlParams.get('utm_source') || '',
                medium: urlParams.get('utm_medium') || '',
                campaign: urlParams.get('utm_campaign') || '',
                term: urlParams.get('utm_term') || '',
                content: urlParams.get('utm_content') || ''
            },
            fbclid: urlParams.get('fbclid') || '',
            gclid: urlParams.get('gclid') || ''
        };
    }

    /**
     * Extract concert metadata from page elements
     */
    getConcertMetadata() {
        // Get page-level data first
        const pageContainer = document.querySelector('[data-page-template]');
        const pageData = {
            template: pageContainer?.dataset.pageTemplate || '',
            postId: pageContainer?.dataset.postId || '',
            campaign: pageContainer?.dataset.campaign || ''
        };

        // Get all concerts on the page and group by concert ID
        const concertElements = document.querySelectorAll('[data-concert-id]');
        const concertsMap = new Map();
        const venues = new Set();
        const dates = new Set();

        concertElements.forEach(element => {
            const concertId = element.dataset.concertId || '';
            const title = element.dataset.concertTitle || '';
            const date = element.dataset.concertDate || '';
            const venue = element.dataset.concertVenue || '';
            const category = element.dataset.concertCategory || '';
            const ticketUrl = element.dataset.ticketUrl || '';
            const previewUrl = element.dataset.previewUrl || '';

            // Only add to map if we have a concert ID and title (to avoid empty duplicates)
            if (concertId && title) {
                // Parse multiple ticket URLs if available
                let ticketUrls = [];
                const ticketUrlsData = element.dataset.ticketUrls;
                if (ticketUrlsData) {
                    try {
                        ticketUrls = JSON.parse(ticketUrlsData);
                    } catch (e) {
                        // Fallback to single ticket URL
                        if (ticketUrl) ticketUrls = [ticketUrl];
                    }
                } else if (ticketUrl) {
                    ticketUrls = [ticketUrl];
                }

                const performanceCount = parseInt(element.dataset.performanceCount) || ticketUrls.length || 1;

                concertsMap.set(concertId, {
                    concertId: concertId,
                    title: title,
                    date: date,
                    venue: venue,
                    category: category,
                    ticketUrl: ticketUrl, // Keep first URL for backwards compatibility
                    ticketUrls: ticketUrls, // All ticket URLs array
                    performanceCount: performanceCount,
                    previewUrl: previewUrl
                });
                
                if (venue) venues.add(venue);
                if (date) dates.add(date);
            }
        });

        // Convert map to array
        const concerts = Array.from(concertsMap.values());

        // Return comprehensive concert data
        return {
            // Page-level data
            pageTemplate: pageData.template,
            postId: pageData.postId,
            campaign: pageData.campaign,
            
            // Concert summary data
            concertCount: concerts.length,
            concertIds: concerts.map(c => c.concertId).filter(id => id),
            concertTitles: concerts.map(c => c.title).filter(title => title),
            venueNames: Array.from(venues),
            performanceDates: Array.from(dates),
            
            // First concert data (for backwards compatibility)
            concertId: concerts[0]?.concertId || '',
            title: concerts[0]?.title || '',
            date: concerts[0]?.date || '',
            venue: concerts[0]?.venue || '',
            category: concerts[0]?.category || '',
            ticketUrl: concerts[0]?.ticketUrl || '',
            
            // All concerts array for detailed tracking
            allConcerts: concerts
        };
    }

    /**
     * Determine the post type
     */
    getPostType() {
        const bodyClasses = document.body.className;
        if (bodyClasses.includes('single-ads-landing-page')) {
            return 'ads-landing-page';
        }
        if (bodyClasses.includes('page')) {
            return 'page';
        }
        if (bodyClasses.includes('single')) {
            return 'post';
        }
        return 'unknown';
    }

    /**
     * Determine the page type for analytics
     */
    getPageType() {
        const postType = this.getPostType();
        if (postType === 'ads-landing-page') {
            return 'landing_page';
        }
        return 'website';
    }

    /**
     * Configure GA4 cross-domain tracking
     */
    configureCrossDomainTracking() {
        if (typeof gtag !== 'function') {
            this.log('gtag not available for cross-domain configuration');
            return;
        }

        // Configure cross-domain linker for Ticketmaster domains
        const crossDomains = [
            'ticketmaster.com',
            'www.ticketmaster.com',
            'concerts.livenation.com',
            'www.livenation.com'
        ];

        // Configure GA4 cross-domain tracking if GA tracking ID is available
        if (this.gaTrackingId) {
            gtag('config', this.gaTrackingId, {
                linker: {
                    domains: crossDomains
                },
                transport_type: 'beacon',
                custom_map: {
                    custom_parameter_1: 'concert_id',
                    custom_parameter_2: 'venue_name'
                }
            });

            this.log('Cross-domain tracking configured for:', crossDomains);
        } else {
            this.log('GA4 Tracking ID not configured, cross-domain tracking unavailable');
        }
    }

    /**
     * Add GA linker parameters to outbound URLs
     */
    addLinkerParams(url) {
        if (typeof gtag !== 'function') {
            return url;
        }

        try {
            // Check if URL is a cross-domain target
            const urlObj = new URL(url);
            const crossDomains = ['ticketmaster.com', 'livenation.com'];
            const isDomainMatch = crossDomains.some(domain => 
                urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
            );

            if (isDomainMatch && this.gaTrackingId) {
                // Get linker parameter from GA4
                gtag('get', this.gaTrackingId, 'linker_param', (linkerParam) => {
                    if (linkerParam) {
                        const separator = url.includes('?') ? '&' : '?';
                        const enhancedUrl = url + separator + linkerParam;
                        this.log('Added GA linker params to URL:', enhancedUrl);
                        return enhancedUrl;
                    }
                });
            }
        } catch (error) {
            this.log('Error adding linker params:', error);
        }

        return url;
    }

    /**
     * Load Google Tag Manager
     */
    loadGTM() {
        if (!this.gtmContainerId || window.gtmLoaded) { // Prevent multiple loads
            return;
        }

        // Create GTM script
        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmContainerId}`;
        
        // Insert GTM initialization code
        const gtmInitScript = document.createElement('script');
        gtmInitScript.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${this.gtmContainerId}');
        `;
        
        document.head.appendChild(gtmInitScript);
        document.head.appendChild(gtmScript);
        
        // Add noscript fallback to body
        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${this.gtmContainerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.appendChild(noscript);
        
        window.gtmLoaded = true;
        this.log('GTM loaded with container ID:', this.gtmContainerId);

        // Configure cross-domain tracking after GTM loads
        setTimeout(() => {
            this.configureCrossDomainTracking();
        }, 1000);
    }

    /**
     * Set up event listeners for user interactions
     */
    bindEvents() {
        // Track page view for landing pages
        const concertMetadata = this.getConcertMetadata();
         console.log('Concert Metadata:', concertMetadata);
     
        if (concertMetadata.concertCount > 0) {
            // Track view_item for the first/featured concert
            this.trackViewItem(concertMetadata);
        }

        // Email form submission
     
        const emailForm = document.querySelector('#kso-email-signup-form');
        if (emailForm) {
            emailForm.addEventListener('submit', () => {
                this.trackEvent('email_signup_attempt', {
                    form_location: 'landing_page',
                    page_url: window.location.href
                });
            });
        }

        // Concert card interactions - Event Details buttons
         
        document.querySelectorAll('[data-action="event-details"]').forEach(button => {
            button.addEventListener('click', (e) => {
                const concertId = button.dataset.concertId;
                const concertData = this.getConcertDataById(concertId);
                if (concertData) {
                    this.trackSelectItem(concertData, 'event_details');
                }
            });
        });

        // Concert card interactions - Buy Tickets buttons (Add to Cart)
  
        document.querySelectorAll('[data-action="open-ticket-modal"]').forEach(button => {
            button.addEventListener('click', (e) => {
                const concertId = button.dataset.concertId;
                const concertData = this.getConcertDataById(concertId);
                if (concertData) {
                    this.trackAddToCart(concertData);
                }
            });
        });

        // Hero CTA tracking
        document.querySelectorAll('[data-action="call"]').forEach(button => {
            button.addEventListener('click', (e) => {
                const eventData = {
                    cta_label: 'Call Box Office',
                    // cta_phone: button.dataset.phone || '+1-865-523-1178', // Removed for compliance
                    cta_location: button.dataset.location || 'hero',
                    campaign: concertMetadata.campaign || '',
                    value: 25.00 // Estimated call value
                };

                

                // Meta Pixel Custom Event (removed phone_number for compliance)
                this.trackMetaCustomEvent('KSO_CallInitiated', {
                    cta_location: eventData.cta_location,
                    value: eventData.value,
                    currency: 'USD',
                    content_name: 'Box Office Call',
                    content_category: 'customer_service'
                });

                // Custom Event
                this.trackEvent('kso_call_box_office', eventData);
            });
        });

        document.querySelectorAll('[data-action="scroll-to-signup"]').forEach(button => {
            button.addEventListener('click', (e) => {
                const eventData = {
                    cta_label: 'Get Alerts',
                    cta_location: button.dataset.location || 'hero',
                    campaign: concertMetadata.campaign || ''
                };

                // Meta Pixel Custom Event
                this.trackMetaCustomEvent('KSO_GetAlertsClicked', {
                    cta_location: eventData.cta_location,
                    campaign: eventData.campaign
                });

                // Custom Event
                this.trackEvent('kso_get_alerts_clicked', eventData);
            });
        });

        // Outbound ticket links (Begin Checkout)
        document.querySelectorAll('a[href*="ticketmaster"], a[href*="tickets"]').forEach(link => {
            link.addEventListener('click', (e) => {
                // Prevent immediate navigation for cross-domain tracking
                e.preventDefault();
                
                const originalUrl = link.href;
                const concertCard = link.closest('[data-concert-id]');
                
                if (concertCard) {
                    const concertId = concertCard.dataset.concertId;
                    const concertData = this.getConcertDataById(concertId);
                    if (concertData) {

                        //  I'll be back here
                        // Track begin_checkout with cross-domain handling
                        this.trackBeginCheckout(concertData);
                        
                        // Add linker parameters to URL
                        const enhancedUrl = this.addLinkerParams(originalUrl);
                        
                        // Navigate after a brief delay to ensure tracking completes
                        setTimeout(() => {
                            window.open(enhancedUrl, '_blank');
                        }, 100);
                    } else {
                        // No concert data, navigate immediately
                        window.open(originalUrl, '_blank');
                    }
                } else {
                    // No concert context, navigate immediately
                    window.open(originalUrl, '_blank');
                }
                
                // Track generic outbound click
                this.trackEvent('kso_outbound_click', {
                    link_domain: new URL(originalUrl).hostname,
                    link_url: originalUrl,
                    destination: 'ticketmaster',
                    cross_domain_configured: true
                });
            });
        });

        // Legacy ticket button support
        document.querySelectorAll('[data-track="ticket_click"], .ticket-button, .buy-tickets').forEach(button => {
            button.addEventListener('click', (e) => {
                const concertCard = button.closest('[data-concert-id]');
                if (concertCard) {
                    const concertId = concertCard.dataset.concertId;
                    const concertData = this.getConcertDataById(concertId);
                    if (concertData) {
                        this.trackSelectItem(concertData, 'legacy_button');
                    }
                }
            });
        });
    }

    /**
     * Track custom events
     */
    trackEvent(eventName, eventData = {}) {
        const eventPayload = {
            event: eventName,
            ...eventData,
            timestamp: new Date().toISOString()
        };

        window.dataLayer.push(eventPayload);
        
        // Use enhanced debug logging
        this.debugLogEvent(eventName, eventPayload, false, false);
    }

    /**
     * Debug logging (only shows on .local domains when debug mode enabled)
     */
    log(...args) {
        if (this.isDebugMode && window.location.hostname.includes('.local')) {
            console.log('[KSO Analytics]', ...args);
            
            // Also log to debug console if it exists
            if (window.ksoDebugConsole) {
                window.ksoDebugConsole.addLogEntry('info', args.join(' '));
            }
        }
    }

    /**
     * Enhanced debug logging with event tracking
     */
    debugLogEvent(eventName, eventData, ga4Status = false, metaStatus = false) {
        if (!this.isDebugMode || !window.location.hostname.includes('.local')) {
            return;
        }

        // Console logging with enhanced formatting
        console.group(`🎵 Analytics Event: ${eventName}`);
        console.log('📊 Data:', eventData);
        console.log(`✅ GA4: ${ga4Status ? 'Sent' : 'Not Available'} | ✅ Meta: ${metaStatus ? 'Sent' : 'Not Available'}`);
        console.log('⏰ Timestamp:', new Date().toISOString());
        console.groupEnd();

        // Send to debug console UI if available
        if (window.ksoDebugConsole) {
            window.ksoDebugConsole.addEvent({
                eventName,
                eventData,
                ga4Status,
                metaStatus,
                timestamp: new Date().toISOString()
            });
            window.ksoDebugConsole.addLogEntry('info', `${eventName} event tracked`);
        }

        // Send to server-side logging
        this.logToServer(eventName, eventData, ga4Status, metaStatus);
    }

    /**
     * Send analytics event to server for logging
     */
    async logToServer(eventName, eventData, ga4Status, metaStatus) {
        if (!this.isDebugMode || !window.location.hostname.includes('.local')) {
            return;
        }

        try {
            const formData = new FormData();
            formData.append('action', 'kso_log_analytics_event');
            formData.append('nonce', this.generateDebugNonce());
            formData.append('event_type', eventName);
            formData.append('event_data', JSON.stringify(eventData));
            formData.append('page_url', window.location.href);
            formData.append('ga4_fired', ga4Status);
            formData.append('meta_fired', metaStatus);

            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                console.warn('Failed to log analytics event to server:', response.statusText);
            }
        } catch (error) {
            console.warn('Error logging analytics event to server:', error);
        }
    }

    /**
     * Generate debug nonce for server logging
     */
    generateDebugNonce() {
        // Simple nonce generation for debug purposes
        return btoa(Date.now().toString()).substring(0, 10);
    }

    /**
     * Track email signup success (called by existing email form code)
     */
    trackEmailSignup(email, campaignData = {}) {
        const concertData = this.getConcertMetadata();
        const pageData = this.getPageMetadata(); // Get current page UTM data
        
        // Prepare UTM data for Meta Pixel
        const utmData = {
            utm_source: campaignData.source || pageData.utm.source || '',
            utm_medium: campaignData.medium || pageData.utm.medium || '',
            utm_campaign: campaignData.campaign_utm || pageData.utm.campaign || '',
            utm_content: campaignData.content || pageData.utm.content || '',
            utm_term: campaignData.term || pageData.utm.term || ''
        };

        // Meta Pixel Lead Event
        this.trackMetaLead(utmData);

        // Custom Event
        this.trackEvent('email_signup_success', {
            email_address: email,
            form_location: 'landing_page',
            campaign: campaignData.campaign || pageData.utm.campaign || '',
            ...utmData,
            concert_id: concertData.concertId || '',
            concert_title: concertData.title || '',
            page_url: window.location.href,
            // Add page-level tracking data for better attribution
            page_title: pageData.title,
            referrer: document.referrer || 'direct'
        });
    }

    /**
     * Track GA4 Enhanced E-commerce: View Item (concert page view)
     */
    trackViewItem(concertData) {
        const value = this.estimateTicketValue(concertData.ticketUrls?.length || 1);
        
        // GA4 Event
        if (typeof gtag === 'function') { // Ensure gtag is available
            const eventData = {
                currency: 'USD',
                value: value,
                items: [{
                    item_id: concertData.concertId,
                    item_name: concertData.title,
                    item_category: 'concert_ticket',
                    item_category2: concertData.category || 'symphony',
                    item_variant: concertData.venue,
                    location_id: concertData.venue,
                    price: this.estimateTicketValue(1),
                    quantity: 1
                }]
            };

            console.log('Logging view_item with data:', eventData);
            
            gtag('event', 'view_item', eventData);
        } else {
            this.log('gtag not available for view_item event');
        }

        // Meta Pixel Event
        const metaFired = this.trackMetaViewContent(concertData, value);

        // Enhanced debug logging
        this.debugLogEvent('view_item', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            performance_count: concertData.performanceCount || 1,
            value: value
        }, typeof gtag === 'function', metaFired);

        // Custom Event
        this.trackEvent('kso_view_item', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            performance_count: concertData.performanceCount || 1,
            value: value
        });
    }

    /**
     * Track GA4 Enhanced E-commerce: Select Item (concert card click)
     */
    trackSelectItem(concertData, clickLocation = 'card') {
        const value = this.estimateTicketValue(concertData.ticketUrls?.length || 1);
        
        // GA4 Event
        if (typeof gtag === 'function') {
            const eventData = {
                currency: 'USD',
                value: value,
                items: [{
                    item_id: concertData.concertId,
                    item_name: concertData.title,
                    item_category: 'concert_ticket',
                    item_category2: concertData.category || 'symphony',
                    item_variant: concertData.venue,
                    location_id: concertData.venue,
                    price: this.estimateTicketValue(1),
                    quantity: 1
                }]
            };
            gtag('event', 'select_item', eventData);
        } else {
            this.log('gtag not available for select_item event');
        }

        // Meta Pixel Custom Event
        this.trackMetaCustomEvent('KSO_ConcertInteraction', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            interaction_type: 'select_item',
            click_location: clickLocation,
            value: value,
            currency: 'USD'
        });

        // Custom Event
        this.trackEvent('kso_select_item', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            click_location: clickLocation,
            performance_count: concertData.performanceCount || 1,
            value: value
        });
    }

    /**
     * Track GA4 Enhanced E-commerce: Add to Cart (ticket modal opened)
     */
    trackAddToCart(concertData, performanceIndex = 0) {
        const ticketUrl = Array.isArray(concertData.ticketUrls) 
            ? concertData.ticketUrls[performanceIndex] || concertData.ticketUrls[0]
            : concertData.ticketUrl;
        
        const value = this.estimateTicketValue(1);

        // GA4 Event
        if (typeof gtag === 'function') {
            const eventData = {
                currency: 'USD',
                value: value,
                items: [{
                    item_id: `${concertData.concertId}_${performanceIndex}`,
                    item_name: concertData.title,
                    item_category: 'concert_ticket',
                    item_category2: concertData.category || 'symphony',
                    item_variant: concertData.venue,
                    location_id: concertData.venue,
                    price: this.estimateTicketValue(1),
                    quantity: 1
                }]
            };
            gtag('event', 'add_to_cart', eventData);
        } else {
            this.log('gtag not available for add_to_cart event');
        }

        // Meta Pixel Event
        this.trackMetaAddToCart(concertData, value);

        // Custom Event
        this.trackEvent('kso_add_to_cart', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            performance_index: performanceIndex,
            ticket_url: ticketUrl,
            modal_opened: true,
            value: value
        });
    }

    /**
     * Track GA4 Enhanced E-commerce: Begin Checkout (ticket link clicked)
     */
    trackBeginCheckout(concertData, performanceIndex = 0) {
        const ticketUrl = Array.isArray(concertData.ticketUrls) 
            ? concertData.ticketUrls[performanceIndex] || concertData.ticketUrls[0]
            : concertData.ticketUrl;
        
        const value = this.estimateTicketValue(1);

        // GA4 Event with transport_type beacon for cross-domain tracking
        if (typeof gtag === 'function') {
            const eventData = {
                currency: 'USD',
                value: value,
                transport_type: 'beacon', // Ensures event fires even as page unloads
                event_callback: () => {
                    this.log('Begin checkout event sent, ready for cross-domain navigation');
                },
                items: [{
                    item_id: `${concertData.concertId}_${performanceIndex}`,
                    item_name: concertData.title,
                    item_category: 'concert_ticket',
                    item_category2: concertData.category || 'symphony',
                    item_variant: concertData.venue,
                    location_id: concertData.venue,
                    price: this.estimateTicketValue(1),
                    quantity: 1
                }],
                // Add custom parameters for cross-domain context
                concert_id: concertData.concertId,
                venue_name: concertData.venue,
                campaign_source: this.getPageMetadata().utm.source || 'direct'
            };
            gtag('event', 'begin_checkout', eventData);
        } else {
            this.log('gtag not available for begin_checkout event');
        }

        // Meta Pixel Event
        this.trackMetaInitiateCheckout(concertData, value);

        // Custom Event
        this.trackEvent('kso_begin_checkout', {
            concert_id: concertData.concertId,
            concert_title: concertData.title,
            venue_name: concertData.venue,
            performance_index: performanceIndex,
            ticket_url: ticketUrl,
            destination: 'ticketmaster',
            value: value,
            cross_domain_ready: true
        });
    }

    /**
     * Track Meta Pixel: ViewContent
     */
    trackMetaViewContent(concertData, value) {
        if (typeof fbq !== 'function') {
            this.log('fbq not available for ViewContent event');
            return false;
        }

        const eventId = this.generateEventId();
        fbq('track', 'ViewContent', {
            event_id: eventId,
            content_name: concertData.title,
            content_category: 'concert_ticket',
            content_type: 'product',
            content_ids: [concertData.concertId],
            value: value,
            currency: 'USD',
            // Custom parameters for audience building
            concert_venue: concertData.venue,
            concert_category: concertData.category || 'symphony',
            performance_count: concertData.performanceCount || 1
        });

        this.log('Meta Pixel ViewContent tracked', { concertId: concertData.concertId, value });
        return true;
    }

    /**
     * Track Meta Pixel: AddToCart
     */
    trackMetaAddToCart(concertData, value) {
        if (typeof fbq !== 'function') {
            this.log('fbq not available for AddToCart event');
            return false;
        }

        const eventId = this.generateEventId();
        fbq('track', 'AddToCart', {
            event_id: eventId,
            content_name: concertData.title,
            content_category: 'concert_ticket',
            content_type: 'product',
            content_ids: [concertData.concertId],
            value: value,
            currency: 'USD',
            // Custom parameters for audience building
            concert_venue: concertData.venue,
            concert_category: concertData.category || 'symphony',
            performance_count: concertData.performanceCount || 1
        });

        this.log('Meta Pixel AddToCart tracked', { concertId: concertData.concertId, value });
        return true;
    }

    /**
     * Track Meta Pixel: InitiateCheckout
     */
    trackMetaInitiateCheckout(concertData, value) {
        if (typeof fbq !== 'function') {
            this.log('fbq not available for InitiateCheckout event');
            return false;
        }

        const eventId = this.generateEventId();
        fbq('track', 'InitiateCheckout', {
            event_id: eventId,
            content_name: concertData.title,
            content_category: 'concert_ticket',
            content_type: 'product',
            content_ids: [concertData.concertId],
            value: value,
            currency: 'USD',
            // Custom parameters for audience building
            concert_venue: concertData.venue,
            concert_category: concertData.category || 'symphony',
            performance_count: concertData.performanceCount || 1
        });

        this.log('Meta Pixel InitiateCheckout tracked', { concertId: concertData.concertId, value });
        return true;
    }

    /**
     * Track Meta Pixel: Lead (for email signups)
     */
    trackMetaLead(campaignData = {}) {
        if (typeof fbq !== 'function') {
            this.log('fbq not available for Lead event');
            return false;
        }

        const eventId = this.generateEventId();
        fbq('track', 'Lead', {
            event_id: eventId,
            content_name: 'Concert Email Signup',
            content_category: 'email_subscription',
            value: 5.00, // Lead value
            currency: 'USD',
            // Campaign attribution
            source: campaignData.utm_source || '',
            medium: campaignData.utm_medium || '',
            campaign: campaignData.utm_campaign || ''
        });

        this.log('Meta Pixel Lead tracked', { campaign: campaignData.utm_campaign });
        return true;
    }

    /**
     * Track Custom Meta Events for specific KSO actions
     */
    trackMetaCustomEvent(eventName, eventData = {}) {
        if (typeof fbq !== 'function') { // Ensure fbq is available
            this.log(`fbq not available for custom event: ${eventName}`);
            return false;
        }

        const eventId = this.generateEventId();
        fbq('trackCustom', eventName, {
            event_id: eventId,
            ...eventData
        });

        this.log(`Meta Pixel custom event tracked: ${eventName}`, eventData);
        return true;
    }

    /**
     * Generate unique event ID for deduplication
     */
    generateEventId() {
        return 'kso_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Estimate ticket value for e-commerce tracking
     */
    estimateTicketValue(quantity = 1) {
        // Base ticket price estimate - can be configured per venue/concert type
        const basePrice = 45; // Average KSO ticket price
        return basePrice * quantity;
    }

    /**
     * Get concert data by ID for tracking
     */
    getConcertDataById(concertId) {
        const concertData = this.getConcertMetadata();
        return concertData.allConcerts?.find(c => c.concertId === concertId) || 
               (concertData.concertId === concertId ? concertData : null);
    }

    /**
     * Get current page data for external use
     */
    getPageData() {
        return {
            page: this.getPageMetadata(),
            concert: this.getConcertMetadata()
        };
    }
}

// Debug Console for Local Development
class KSODebugConsole {
    constructor() {
        this.events = [];
        this.logs = [];
        this.isVisible = false;
        this.createConsoleUI();
    }

    createConsoleUI() {
        // Only create on .local domains
        if (!window.location.hostname.includes('.local')) {
            return;
        }

        // Create console container
        this.console = document.createElement('div');
        this.console.id = 'kso-debug-console';
        this.console.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            width: 400px;
            max-height: 500px;
            background: #1a1a1a;
            color: #fff;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            border: 2px solid #333;
            border-radius: 8px;
            z-index: 10000;
            display: none;
            overflow: hidden;
        `;

        // Create header
        const header = document.createElement('div');
        header.style.cssText = `
            background: #333;
            padding: 8px 12px;
            font-weight: bold;
            cursor: move;
            display: flex;
            justify-content: space-between;
            align-items: center;
        `;
        header.innerHTML = `
            <span>🎵 KSO Analytics Debug</span>
            <div>
                <button id="kso-debug-clear" style="background: #666; color: #fff; border: none; padding: 2px 6px; margin-right: 4px; cursor: pointer;">Clear</button>
                <button id="kso-debug-close" style="background: #e74c3c; color: #fff; border: none; padding: 2px 6px; cursor: pointer;">×</button>
            </div>
        `;

        // Create content area
        this.content = document.createElement('div');
        this.content.style.cssText = `
            padding: 12px;
            max-height: 400px;
            overflow-y: auto;
        `;

        this.console.appendChild(header);
        this.console.appendChild(this.content);
        document.body.appendChild(this.console);

        // Add event listeners
        header.querySelector('#kso-debug-clear').addEventListener('click', () => this.clearLogs());
        header.querySelector('#kso-debug-close').addEventListener('click', () => this.hide());

        // Make draggable
        this.makeDraggable(header);

        // Create toggle button
        this.createToggleButton();
    }

    createToggleButton() {
        const toggle = document.createElement('div');
        toggle.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            width: 50px;
            height: 50px;
            background: #2c3e50;
            color: #fff;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 9999;
            font-size: 20px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        `;
        toggle.innerHTML = '🎵';
        toggle.title = 'Toggle KSO Analytics Debug Console';
        toggle.addEventListener('click', () => this.toggle());
        document.body.appendChild(toggle);

        this.toggleButton = toggle;
    }

    makeDraggable(element) {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        element.addEventListener('mousedown', (e) => {
            e.preventDefault();
            pos3 = e.clientX;
            pos4 = e.clientY;
            document.addEventListener('mouseup', closeDragElement);
            document.addEventListener('mousemove', elementDrag);
        });

        const elementDrag = (e) => {
            e.preventDefault();
            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;
            this.console.style.top = (this.console.offsetTop - pos2) + 'px';
            this.console.style.left = (this.console.offsetLeft - pos1) + 'px';
        };

        const closeDragElement = () => {
            document.removeEventListener('mouseup', closeDragElement);
            document.removeEventListener('mousemove', elementDrag);
        };
    }

    show() {
        this.console.style.display = 'block';
        this.toggleButton.style.display = 'none';
        this.isVisible = true;
    }

    hide() {
        this.console.style.display = 'none';
        this.toggleButton.style.display = 'flex';
        this.isVisible = false;
    }

    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    addEvent(eventInfo) {
        this.events.push(eventInfo);
        this.renderEvent(eventInfo);
        this.updateToggleIndicator();
    }

    addLogEntry(level, message) {
        const logEntry = { level, message, timestamp: new Date().toISOString() };
        this.logs.push(logEntry);
        this.renderLog(logEntry);
    }

    renderEvent(eventInfo) {
        const eventDiv = document.createElement('div');
        eventDiv.style.cssText = `
            margin-bottom: 8px;
            padding: 8px;
            background: #2c3e50;
            border-radius: 4px;
            border-left: 3px solid #3498db;
        `;

        const statusColor = eventInfo.ga4Status || eventInfo.metaStatus ? '#27ae60' : '#f39c12';
        
        eventDiv.innerHTML = `
            <div style="font-weight: bold; color: #3498db;">${eventInfo.eventName}</div>
            <div style="margin: 4px 0; font-size: 11px;">
                <span style="color: ${statusColor};">
                    GA4: ${eventInfo.ga4Status ? '✅' : '❌'} | 
                    Meta: ${eventInfo.metaStatus ? '✅' : '❌'}
                </span>
            </div>
            <div style="color: #bdc3c7; font-size: 10px;">${new Date(eventInfo.timestamp).toLocaleTimeString()}</div>
        `;

        this.content.appendChild(eventDiv);
        this.content.scrollTop = this.content.scrollHeight;
    }

    renderLog(logEntry) {
        const logDiv = document.createElement('div');
        logDiv.style.cssText = `
            margin-bottom: 4px;
            padding: 4px;
            font-size: 10px;
            opacity: 0.8;
        `;

        const levelColor = logEntry.level === 'error' ? '#e74c3c' : '#95a5a6';
        
        logDiv.innerHTML = `
            <span style="color: ${levelColor};">[${logEntry.level.toUpperCase()}]</span>
            <span style="color: #ecf0f1;">${logEntry.message}</span>
        `;

        this.content.appendChild(logDiv);
        this.content.scrollTop = this.content.scrollHeight;
    }

    clearLogs() {
        this.events = [];
        this.logs = [];
        this.content.innerHTML = '';
        this.updateToggleIndicator();
    }

    updateToggleIndicator() {
        const count = this.events.length;
        this.toggleButton.style.background = count > 0 ? '#27ae60' : '#2c3e50';
        if (count > 0) {
            this.toggleButton.innerHTML = count > 9 ? '9+' : count.toString();
        } else {
            this.toggleButton.innerHTML = '🎵';
        }
    }
}

// Initialize analytics when DOM is ready
function initializeKSOAnalytics() {
    // Initialize main analytics system
    window.ksoAnalytics = new KSOAnalytics();
    
    // Create debug console for .local domains
    if (window.location.hostname.includes('.local')) {
        window.ksoDebugConsole = new KSODebugConsole();
    }
    
    // Expose methods globally for existing code integration
    window.ksoTrackSelectItem = (concertId, location = 'unknown') => {
        const concertData = window.ksoAnalytics.getConcertDataById(concertId);
        if (concertData) {
            window.ksoAnalytics.trackSelectItem(concertData, location);
        }
    };
    
    window.ksoTrackAddToCart = (concertId, performanceIndex = 0) => {
        const concertData = window.ksoAnalytics.getConcertDataById(concertId);
        if (concertData) {
            window.ksoAnalytics.trackAddToCart(concertData, performanceIndex);
        }
    };
    
    window.ksoTrackBeginCheckout = (concertId, performanceIndex = 0) => {
        const concertData = window.ksoAnalytics.getConcertDataById(concertId);
        if (concertData) {
            window.ksoAnalytics.trackBeginCheckout(concertData, performanceIndex);
        }
    };

    console.log('🎵 KSO Analytics initialized via webpack');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeKSOAnalytics);
} else {
    // DOM already loaded
    initializeKSOAnalytics();
}

// Export classes for module use
export { KSOAnalytics, KSODebugConsole };